import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

type DecisionMaker = StarterDashboardV2PageData['petition']['decisionMakersConnection']['nodes'][number];

type UseDecisionMakersListReturn = ModelHookResult<
	{
		petitionEditPage: string;
		dmSources: 'none' | 'only_auto_add' | 'partial_manual_add' | 'no_email_dms';
		decisionMakers: readonly DecisionMaker[];
	},
	{
		trackAddOrEditDmsClick: () => void;
		trackView: () => void;
	}
>;

export function useDecisionMakersList(): UseDecisionMakersListReturn {
	const track = useTracking();

	const {
		data: {
			petition: {
				slug,
				decisionMakersConnection: { nodes: decisionMakers },
			},
		},
	} = useStarterDashboardV2PageData();
	const petitionEditPg = `/p/${slug}/edit#decision_makers`;

	const getDmSources = () => {
		if (decisionMakers.length === 0) return 'none';
		if (decisionMakers.some((dm) => !dm.targetPetitionAssociationData?.isAutoAdded)) return 'partial_manual_add';
		if (decisionMakers.every((dm) => !dm.email)) return 'no_email_dms';
		return 'only_auto_add';
	};
	const dmSources = getDmSources();

	const trackAddOrEditDmsClick = useCallback(() => {
		void track('starter_dashboard_add_or_edit_decision_makers_click', {
			cta: decisionMakers.length > 0 ? 'edit_dms' : 'add_dms',
			current_dm_sources: dmSources,
		});
	}, [track, decisionMakers, dmSources]);

	const trackView = useCallback(() => {
		void track('starter_dashboard_decision_makers_list_view', {
			decision_maker_ids: decisionMakers.map((dm) => dm.id),
			cta: decisionMakers.length > 0 ? 'edit_dms' : 'add_dms',
			current_dm_sources: dmSources,
		});
	}, [track, decisionMakers, dmSources]);

	return {
		data: {
			petitionEditPage: petitionEditPg,
			decisionMakers,
			dmSources,
		},
		actions: {
			trackAddOrEditDmsClick,
			trackView,
		},
	};
}
